import type { BrandCode } from "brandRegionConfig";
import type { PitstopRoutes } from "core/entities/AppTypes";
import type { FixHubPermissions } from "resources/permissions";

interface PagePermission {
  /** If set, at least one of these permissions must be granted to the user */
  hasSomePermissions?: FixHubPermissions[]; //
  /** If set, all of these permissions must be granted to the user */
  hasAllPermissions?: FixHubPermissions[];
  /** Disables the page for all users */
  disabled?: boolean;
  /** If set, limits the page to the specified brands */
  availableForBrands?: BrandCode[];
}

const PaymentRequestSecurity: PagePermission = {
  hasSomePermissions: ["Fixhub.PaymentRequests"],
};

const CmsEditorSecurity: PagePermission = {
  hasSomePermissions: [
    "CmsKit.BlogPosts.Create",
    "CmsKit.BlogPosts.Update",
    "CmsKit.BlogPosts.Delete",
    "CmsKit.BlogPosts.Publish",
  ],
};

const NewsEventSecurity: PagePermission = {
  hasSomePermissions: ["NewsEvents.Articles", "NewsEvents.Categories"],
};

/**
 * Map of routes to their required permissions.
 * Used by PagePermissionChecker component to determine if a user has access to a page.
 */
export const routeAccessMap = new Map<PitstopRoutes, PagePermission>();

routeAccessMap.set("/[region]/payment-request", PaymentRequestSecurity);
routeAccessMap.set("/[region]/payment-request/add", PaymentRequestSecurity);
routeAccessMap.set(
  "/[region]/payment-request/[paymentRequestId]",
  PaymentRequestSecurity
);
routeAccessMap.set(
  "/[region]/payment-request/[paymentRequestId]/edit",
  PaymentRequestSecurity
);
routeAccessMap.set("/[region]/test-page", {
  // hasAllPermissions: ["Fixhub.DevOptions"],
});
routeAccessMap.set("/[region]/localization-inspect", {
  hasAllPermissions: ["Fixhub.DevOptions"],
});
routeAccessMap.set("/[region]/theme-inspect", {
  hasAllPermissions: ["Fixhub.DevOptions"],
});
routeAccessMap.set("/[region]/estimate-request", {
  hasSomePermissions: ["Fixhub.EstimateRequests"],
});
routeAccessMap.set("/[region]/estimate-request/[id]", {
  hasSomePermissions: ["Fixhub.EstimateRequests"],
});
routeAccessMap.set("/[region]/cms-editor", CmsEditorSecurity);
routeAccessMap.set("/[region]/cms-editor/[id]", CmsEditorSecurity);
routeAccessMap.set("/[region]/cms-editor/create", CmsEditorSecurity);
routeAccessMap.set("/[region]/shop-hours-management", {
  hasSomePermissions: ["SharedEntities.ShopHours"],
});
routeAccessMap.set("/[region]/shop-hours-management/admin", {
  hasAllPermissions: [
    "SharedEntities.ShopHours.Edit",
    "SharedEntities.ShopHours.BrandlessAdmin",
  ],
});
// Publication Management
routeAccessMap.set(
  "/[region]/manage/publications/[[...publicationTab]]",
  NewsEventSecurity
);
routeAccessMap.set("/[region]/manage/publications/news-article/create", {
  hasSomePermissions: ["NewsEvents.Articles.Create"],
});
routeAccessMap.set("/[region]/manage/publications/news-article/[id]", {
  hasSomePermissions: ["NewsEvents.Articles.Edit"],
});
routeAccessMap.set("/[region]/manage/publications/event-article/create", {
  hasSomePermissions: ["NewsEvents.Articles.Create"],
});
routeAccessMap.set("/[region]/manage/publications/event-article/[id]", {
  hasSomePermissions: ["NewsEvents.Articles.Edit"],
});
routeAccessMap.set("/[region]/manage/publications/categories/create", {
  hasSomePermissions: ["NewsEvents.Categories.Create"],
});
routeAccessMap.set("/[region]/manage/publications/categories/[id]", {
  hasSomePermissions: ["NewsEvents.Categories.Edit"],
});
routeAccessMap.set("/[region]/manage/publications/labels/create", {
  hasSomePermissions: ["NewsEvents.Categories.Create"],
});
routeAccessMap.set("/[region]/manage/publications/labels/[id]", {
  hasSomePermissions: ["NewsEvents.Categories.Edit"],
});
// Advertisement Banners
routeAccessMap.set("/[region]/manage/advertisement-banner", {
  hasSomePermissions: ["Fixhub.AdvertisementBanners"],
});
routeAccessMap.set("/[region]/manage/advertisement-banner/create", {
  hasSomePermissions: ["Fixhub.AdvertisementBanners.Create"],
});
routeAccessMap.set("/[region]/manage/advertisement-banner/[id]", {
  hasSomePermissions: ["Fixhub.AdvertisementBanners.Edit"],
});
// Suggestions
routeAccessMap.set("/[region]/manage/suggestions", {
  hasSomePermissions: ["Fixhub.Suggestions"],
});
routeAccessMap.set("/[region]/novus-products", {
  availableForBrands: ["novusglass"],
});
routeAccessMap.set("/[region]/shop-sales/create", {
  hasAllPermissions: [
    "Sales.SaleShop.CanUploadSales",
    "Sales.SaleShop.CanGetShopSalesAdjusments",
    "Sales.ShopsSaleAdjustments.SalesSubmissionUploads",
  ],
});
routeAccessMap.set("/[region]/dms/library", {
  hasSomePermissions: [
    "Fixhub.DocNodeManagementSystem.ManageLibraries",
    "Fixhub.DocNodeManagementSystem.SuperAdmin",
    "Fixhub.DocNodeManagementSystem.ManageContents",
  ],
});
routeAccessMap.set("/[region]/dms/permission-diagnostic", {
  hasSomePermissions: ["Fixhub.DocNodeManagementSystem.SuperAdmin"],
});

routeAccessMap.set("/[region]/power-bi/[workspaceId]/[reportId]", {
  hasSomePermissions: ["SharedEntities.PowerBiReports"],
});

routeAccessMap.set("/[region]/shop-sales/royalty-payment", {
  disabled: true, // page requires additional work before it can be enabled, so we'll leave it hidden for now
});

routeAccessMap.set("/[region]/financial", {
  hasSomePermissions: ["Sales.BusinessCentral"],
});

routeAccessMap.set("/[region]/app-config-info", {
  hasAllPermissions: ["Fixhub.DevOptions"],
});

routeAccessMap.set("/[region]/brand-logo-debug", {
  hasAllPermissions: ["Fixhub.DevOptions"],
});

routeAccessMap.set("/[region]/manage/impersonate", {
  hasSomePermissions: ["AbpIdentity.Users.Impersonation"],
});

routeAccessMap.set("/[region]/manage/nps", {
  hasAllPermissions: ["Fixhub.NpsSurvey.Manage"],
});
routeAccessMap.set("/[region]/manage/nps/[surveyId]/create", {
  hasAllPermissions: ["Fixhub.NpsSurvey.Manage"],
});
routeAccessMap.set("/[region]/manage/nps/[surveyId]", {
  hasAllPermissions: ["Fixhub.NpsSurvey.Manage"],
});
routeAccessMap.set("/[region]/manage/nps/[surveyId]/required-questions", {
  hasAllPermissions: ["Fixhub.NpsSurvey.Admin"],
});
routeAccessMap.set("/[region]/manage/logos", {
  hasSomePermissions: ["Fixhub.PitstopLogos.Manage", "Fixhub.PitstopLogos"],
});

routeAccessMap.set("/[region]/manage/pitstop-alerts", {
  hasAllPermissions: ["Fixhub.PitstopAlerts.Manage"],
});
routeAccessMap.set("/[region]/manage/pitstop-alerts/[configId]", {
  hasAllPermissions: ["Fixhub.PitstopAlerts.Manage"],
});

routeAccessMap.set("/[region]/manage/shops", {
  hasAllPermissions: ["Fixhub.PitstopShopManagement.View"],
});
routeAccessMap.set("/[region]/manage/shops/[id]", {
  hasAllPermissions: ["Fixhub.PitstopShopManagement.View"],
});

routeAccessMap.set("/[region]/manage/shops/[id]/change-request", {
  hasAllPermissions: ["Fixhub.PitstopShopManagement.RequestChange"],
});
routeAccessMap.set("/[region]/manage/shops/change-requests", {
  hasAllPermissions: ["Fixhub.PitstopShopManagement.RequestChange"],
});
